/**
 * Pinia Store: Forms Entry
 *
 * Migrated from Vuex: src/extensions/forms/store/modules/entry.js
 */

import Vue from 'vue';
import { defineStore } from 'pinia';
import _ from 'lodash';

import FormEntryApi from '@/extensions/forms/store/api/entry';

const form = {
    id: undefined
};

const rules = {
    name: 'required'
};

const ui = {
    form: {
        initialized: false,
        validateOnEnter: false
    }
};

export const useFormsEntryStore = defineStore('formsEntry', {
    state: () => ({
        loading: true,
        formEntriesPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {},
        form: _.cloneDeep(form),
        rules: _.cloneDeep(rules),
        ui: _.cloneDeep(ui)
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter
    },

    actions: {
        // Mutations converted to actions (Pinia pattern)
        addPaginatedFormEntriesToState(data) {
            this.formEntriesPaginated.items = data.items;
            this.formEntriesPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.formEntriesPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.formEntriesPaginated.link = _.cloneDeep(data.config.link);
            this.formEntriesPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.formEntriesPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.formEntriesPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.formEntriesPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.formEntriesPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.formEntriesPaginated.pageInfo.currentPage = currentPage;
        },

        defineSort({ key, value }) {
            Vue.set(this.formEntriesPaginated.variables, 'order', key);
            Vue.set(this.formEntriesPaginated.variables, 'direction', value);
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.formEntriesPaginated.filters, key);
            } else {
                Vue.set(this.formEntriesPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            this.formEntriesPaginated.showFilters = value;
        },

        paginationBulkSelectionReset() {
            this.formEntriesPaginated.pageInfo.selection = {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            };
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(rules) {
            this.rules = rules;
        },

        resetRules() {
            this.rules = _.cloneDeep(rules);
        },

        resetUI() {
            this.ui = _.cloneDeep(ui);
        },

        setFormInitialized(flag) {
            this.ui.form.initialized = flag;
        },

        setFormValidateOnEnter(flag) {
            this.ui.form.validateOnEnter = flag;
        },

        paginationBulkSelectionCheckAll({ value }) {
            let selection = this.formEntriesPaginated.pageInfo.selection;
            if (value === true) {
                selection.type = 'exclusive';
                selection.identifiers = [];
                selection.total = this.formEntriesPaginated.pageInfo.total;
            } else {
                selection.type = 'inclusive';
                selection.identifiers = [];
                selection.total = selection.identifiers.length;
            }
        },

        paginationBulkSelectionCheckVisible({ value }) {
            const items = _.get(this.formEntriesPaginated, 'items') || [];
            const ids = items.map(el => parseInt(el.id));

            let selection = this.formEntriesPaginated.pageInfo.selection;
            if (value === true) {
                selection.type = 'inclusive';

                if (!selection.identifiers.length) {
                    selection.identifiers = ids;
                } else {
                    selection.identifiers = [...selection.identifiers, ...ids];
                }
            } else {
                selection.type = 'inclusive';
                selection.identifiers = selection.identifiers.filter(el => ids.includes(el) === false);
            }

            selection.identifiers = [...new Set(selection.identifiers)];
            selection.total = selection.identifiers.length;
        },

        paginationBulkSelectionCheckItem(item) {
            let selection = this.formEntriesPaginated.pageInfo.selection;
            const id = parseInt(item.id);

            let checked = false;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;

                case 'exclusive':
                    checked = !~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
            }

            let push = true;
            let incr = true;
            switch (selection.type) {
                case 'inclusive':
                    push = incr = checked === false;
                    break;

                case 'exclusive':
                    push = checked === true;
                    incr = checked === false;
                    break;
            }

            if (push === true) {
                selection.identifiers.push(id);
            } else {
                const index = selection.identifiers.indexOf(id);
                if (index >= 0) selection.identifiers.splice(index, 1);
            }

            if (incr === true) selection.total = selection.total + 1;
            else selection.total = selection.total - 1;
        },

        insertFormEntry(formEntry) {
            this.form = formEntry;
        },

        // Async actions
        fetchFormEntriesPaginated() {
            return FormEntryApi.fetchFormEntriesPaginated(this.formEntriesPaginated).then(data => {
                this.addPaginatedFormEntriesToState(data);

                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        processPaginationBulkAction(action) {
            return FormEntryApi.processFormEntriesPaginationBulkAction(action, this.formEntriesPaginated);
        },

        reset() {
            this.insertFormEntry(_.cloneDeep(form));
        }
    }
});
